import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, routePath, type CoverageResult } from './api';

type SearchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; result: CoverageResult }
  | { kind: 'error'; message: string };

/** Looks across all customer records while leaving the privacy rule on the server. */
export function FindRoute({ userId }: { userId: number | null }) {
  const [name, setName] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [location, setLocation] = useState('');
  const [search, setSearch] = useState<SearchState>({ kind: 'idle' });
  const requestId = useRef(0);

  useEffect(() => () => void (requestId.current += 1), []);

  function clearResult() {
    requestId.current += 1;
    setSearch({ kind: 'idle' });
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (name.trim().length < 3 || streetAddress.trim().length < 3 || location.trim().length < 2)
      return;
    const currentRequest = ++requestId.current;
    setSearch({ kind: 'loading' });
    try {
      const result = await api.customerCoverage(
        userId,
        name.trim(),
        streetAddress.trim(),
        location.trim(),
      );
      if (currentRequest === requestId.current) setSearch({ kind: 'ready', result });
    } catch (error) {
      if (currentRequest === requestId.current) {
        setSearch({
          kind: 'error',
          message: error instanceof Error ? error.message : 'The check could not be completed.',
        });
      }
    }
  }

  return (
    <section className="find-route panel" aria-labelledby="find-route-title">
      <h2 id="find-route-title">Check a new customer</h2>
      <p>
        Search all Tropicana customer records by business and address. If another distributor has a
        matching customer, their identity and customer details stay private.
      </p>
      <form onSubmit={submit} className="find-route-form">
        <div>
          <label htmlFor="coverage-name">Business name</label>
          <input
            id="coverage-name"
            required
            minLength={3}
            maxLength={200}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              clearResult();
            }}
            placeholder="Market or deli name"
          />
        </div>
        <div>
          <label htmlFor="coverage-street">Street address</label>
          <input
            id="coverage-street"
            required
            minLength={3}
            maxLength={200}
            value={streetAddress}
            onChange={(event) => {
              setStreetAddress(event.target.value);
              clearResult();
            }}
            placeholder="Number and street"
          />
        </div>
        <div>
          <label htmlFor="coverage-location">City or ZIP code</label>
          <input
            id="coverage-location"
            required
            minLength={2}
            maxLength={100}
            value={location}
            onChange={(event) => {
              setLocation(event.target.value);
              clearResult();
            }}
            placeholder="City or ZIP"
          />
        </div>
        <button type="submit" disabled={search.kind === 'loading'}>
          {search.kind === 'loading' ? 'Checking…' : 'Check coverage'}
        </button>
      </form>
      {search.kind === 'error' && <p role="alert">Search failed: {search.message}</p>}
      {search.kind === 'ready' && (
        <div className="find-route-results" role="status">
          {search.result.ownMatches.length > 0 && (
            <div>
              <h3>Matching customer on your routes</h3>
              <ul>
                {search.result.ownMatches.map((match) => (
                  <li key={`${match.distributorId}:${match.stop.id}`}>
                    <a href={routePath(match.distributorId, match.routeNumber)}>
                      {match.stop.name} · {match.stop.streetAddress} · {match.distributorName} ·
                      Route {match.routeNumber}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {search.result.servedElsewhere && (
            <p className="find-route-private">
              A customer matching these details is on another Tropicana distributor’s route. Please
              contact Tropicana for guidance before proceeding.
            </p>
          )}
          {!search.result.servedElsewhere && search.result.ownMatches.length === 0 && (
            <p>
              No matching customer was found. Try another business name or address spelling before
              treating this as a new customer.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
